import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { Ban, Search, ShieldOff, Trash2, Globe2 } from "lucide-react";
import { Header } from "@/components/rawan/Header";
import { Footer } from "@/components/rawan/Footer";
import { BackToDashboard } from "@/components/rawan/BackToDashboard";
import { useAuth } from "@/lib/auth";
import {
  useBlockedCountries,
  searchCountries,
  findCountry,
  getCountryRecord,
  type CountryRecord,
} from "@/lib/blocked-countries";

export const Route = createFileRoute("/admin/blocked-countries")({
  head: () => ({
    meta: [
      { title: "حظر الدول — Rawan UI" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: BlockedCountriesPage,
});

function BlockedCountriesPage() {
  const { isAuthed, ready } = useAuth();
  const navigate = useNavigate();
  const { codes, add, remove, clear } = useBlockedCountries();
  const [query, setQuery] = useState("");

  useEffect(() => {
    if (ready && !isAuthed) navigate({ to: "/login" });
  }, [ready, isAuthed, navigate]);

  const suggestions: CountryRecord[] = useMemo(() => searchCountries(query, 8), [query]);
  const exact = useMemo(() => findCountry(query), [query]);

  const blockedRecords = codes
    .map((c) => getCountryRecord(c) ?? { code: c, ar: c, en: c, fr: c, flag: "🏳️" })
    .sort((a, b) => a.ar.localeCompare(b.ar, "ar"));

  const handleBlockSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const target = exact ?? suggestions[0];
    if (target) {
      add(target.code);
      setQuery("");
    }
  };

  return (
    <div className="min-h-dvh flex flex-col bg-background text-foreground" dir="rtl">
      <Header />
      <main className="flex-1 max-w-3xl w-full mx-auto px-4 py-6">
        <BackToDashboard />

        <div className="flex items-center gap-2 mb-4 mt-2">
          <ShieldOff className="h-6 w-6 text-destructive" />
          <h1 className="text-2xl font-extrabold">حظر الدول</h1>
        </div>
        <p className="text-sm text-muted-foreground mb-5 leading-relaxed">
          ابحث عن دولة بالاسم بأي لغة أو برمزها (مثل <span className="font-mono">US</span>،{" "}
          <span className="font-mono">FR</span>) واضغط <strong>حظر</strong>.
          لن تُعرض قائمة كل الدول — تظهر هنا فقط الدول المحظورة.
        </p>

        {/* Search + Block */}
        <form onSubmit={handleBlockSubmit} className="relative">
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="اسم الدولة أو رمزها (مثل: المغرب، France, US)"
                className="w-full pr-9 pl-3 py-2.5 rounded-lg bg-card border border-border focus:border-primary focus:outline-none text-sm"
                autoComplete="off"
              />
            </div>
            <button
              type="submit"
              disabled={!exact && suggestions.length === 0}
              className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-lg bg-destructive text-destructive-foreground font-bold text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:opacity-90"
            >
              <Ban className="h-4 w-4" /> حظر
            </button>
          </div>

          {/* Suggestions dropdown */}
          {query && suggestions.length > 0 && (
            <ul className="absolute z-20 mt-1 w-full bg-card border border-border rounded-lg shadow-lg max-h-72 overflow-y-auto">
              {suggestions.map((c) => {
                const already = codes.includes(c.code);
                return (
                  <li key={c.code}>
                    <button
                      type="button"
                      onClick={() => {
                        if (!already) add(c.code);
                        setQuery("");
                      }}
                      disabled={already}
                      className="w-full flex items-center justify-between gap-2 px-3 py-2 text-sm hover:bg-accent disabled:opacity-60 disabled:cursor-not-allowed text-right"
                    >
                      <span className="flex items-center gap-2">
                        <span className="text-lg">{c.flag}</span>
                        <span className="font-semibold">{c.ar}</span>
                        <span className="text-xs text-muted-foreground">
                          {c.en} · {c.fr}
                        </span>
                      </span>
                      <span className="font-mono text-xs text-muted-foreground">
                        {already ? "محظورة" : c.code}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
          {query && !exact && suggestions.length === 0 && (
            <p className="mt-2 text-xs text-muted-foreground">لا توجد نتائج مطابقة.</p>
          )}
        </form>

        {/* Blocked list */}
        <section className="mt-6">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-bold flex items-center gap-1.5">
              <Globe2 className="h-4 w-4 text-primary" />
              الدول المحظورة ({blockedRecords.length})
            </h2>
            {blockedRecords.length > 0 && (
              <button
                type="button"
                onClick={() => {
                  if (confirm("هل تريد إلغاء حظر جميع الدول؟")) clear();
                }}
                className="text-xs text-destructive hover:underline"
              >
                إلغاء حظر الكل
              </button>
            )}
          </div>

          {blockedRecords.length === 0 ? (
            <div className="rounded-lg border border-dashed border-border bg-card/50 p-6 text-center text-sm text-muted-foreground">
              لا توجد دول محظورة حالياً.
            </div>
          ) : (
            <ul className="grid gap-2 sm:grid-cols-2">
              {blockedRecords.map((c) => (
                <li
                  key={c.code}
                  className="flex items-center justify-between gap-2 bg-card border border-destructive/30 rounded-lg px-3 py-2"
                >
                  <span className="flex items-center gap-2 min-w-0">
                    <span className="text-xl shrink-0">{c.flag}</span>
                    <span className="min-w-0">
                      <span className="block text-sm font-bold truncate">{c.ar}</span>
                      <span className="block text-[10px] text-muted-foreground font-mono">
                        {c.code} · {c.en}
                      </span>
                    </span>
                  </span>
                  <button
                    type="button"
                    onClick={() => remove(c.code)}
                    title="إلغاء الحظر"
                    className="inline-flex items-center justify-center h-8 w-8 rounded-md text-destructive hover:bg-destructive/10 shrink-0"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </section>
      </main>
      <Footer />
    </div>
  );
}
